"use client";

import { useMemo } from "react";
import { Sparkles, X } from "lucide-react";
import { getDefinition } from "@/lib/catalog";
import { runAllDiagnostics } from "@/lib/diagnostics";
import { useAiPostGeneration } from "@/lib/ai/post-generation";
import { useT } from "@/lib/i18n";
import { useAutoSizeVdropTarget } from "@/lib/settings/auto-size-vdrop-target";
import { useSystemStore } from "@/lib/store";

export function GeneratedSystemBanner() {
  const tr = useT();
  const justPlaced = useAiPostGeneration((s) => s.justPlaced);
  const dismiss = useAiPostGeneration((s) => s.dismiss);
  const system = useSystemStore((s) => s.system);
  const setSelection = useSystemStore((s) => s.setSelection);
  const autoSizeTargetPct = useAutoSizeVdropTarget((s) => s.target);

  const { lengths, parts } = useMemo(() => {
    if (!justPlaced) return { lengths: undefined, parts: undefined };
    const { issues } = runAllDiagnostics(
      system,
      getDefinition,
      undefined,
      undefined,
      autoSizeTargetPct,
    );
    return {
      lengths: issues.find((i) => i.code === "ai-estimated-lengths"),
      parts: issues.find((i) => i.code === "ai-estimated-parts"),
    };
  }, [justPlaced, system, autoSizeTargetPct]);

  const lengthCount = lengths?.cableIds?.length ?? 0;
  const partCount = parts?.nodeIds?.length ?? 0;
  // A hand-drawn system carries no marks, so the flag alone can't surface this.
  if (!justPlaced || (lengthCount === 0 && partCount === 0)) return null;

  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 w-[min(520px,calc(100%-24px))]">
      <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-[var(--radius-md)] bg-[var(--color-surface)] border border-[var(--color-border)] shadow-[0_2px_12px_hsl(30_8%_10%/0.08)]">
        <Sparkles
          aria-hidden
          className="mt-[2px] w-3.5 h-3.5 flex-none text-[var(--color-accent)]"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-[12.5px] leading-snug tracking-tight text-[var(--color-fg)]">
            {tr("canvas.generated.placed")}
          </p>
          {lengthCount > 0 && (
            <p className="text-[11.5px] leading-relaxed text-[var(--color-fg-muted)]">
              <button
                type="button"
                onClick={() =>
                  setSelection({ nodeIds: [], cableIds: lengths?.cableIds ?? [] })
                }
                className="underline decoration-[var(--color-border-strong)] underline-offset-2 text-[var(--color-fg)] hover:decoration-[var(--color-fg)]"
              >
                {lengthCount === 1
                  ? tr("canvas.generated.lengths_one")
                  : tr("canvas.generated.lengths_many", { count: lengthCount })}
              </button>{" "}
              {tr("canvas.generated.lengths_why")}
            </p>
          )}
          {partCount > 0 && (
            <p className="text-[11.5px] leading-relaxed text-[var(--color-fg-muted)]">
              <button
                type="button"
                onClick={() =>
                  setSelection({ nodeIds: parts?.nodeIds ?? [], cableIds: [] })
                }
                className="underline decoration-[var(--color-border-strong)] underline-offset-2 text-[var(--color-fg)] hover:decoration-[var(--color-fg)]"
              >
                {partCount === 1
                  ? tr("canvas.generated.parts_one")
                  : tr("canvas.generated.parts_many", { count: partCount })}
              </button>{" "}
              {tr("canvas.generated.parts_why")}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          title={tr("canvas.generated.dismiss")}
          aria-label={tr("canvas.generated.dismiss")}
          className="flex-none -mr-1 -mt-0.5 p-1 rounded-[var(--radius-xs)] text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)] hover:bg-[var(--color-surface-2)] transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
